import { fillText } from 'components/invoiceImport/invoiceImportText';

/**
 * What the journal says of each error code an invoice can carry (api
 * utils/invoiceImportErrors.js, INVOICE_ERROR_STEPS): the api gives the codes
 * and app.admin words them, so this table is the twin of that catalogue and a
 * test pins its fourteen codes. The fixes follow the api README, § 4.
 */
export const INVOICE_ERROR_TEXTS = {
  ERR_BESTT_FACTURE_INTROUVABLE: {
    label: 'Facture introuvable dans Bestt',
    explanation: 'Bestt dit ne pas connaître ces factures.',
    fix: 'Vérifiez ces factures dans Bestt, puis réessayez.',
  },
  ERR_BESTT_API: {
    label: 'Bestt n’a pas répondu',
    explanation: 'Bestt a échoué sur ces factures.',
    fix: 'Attendez quelques minutes, puis réessayez.',
  },
  ERR_ENTREPRISE_INCONNUE_JOBBIZ: {
    label: 'Client Bestt sans entreprise Jobbiz',
    explanation: 'Aucune entreprise Jobbiz n’a ce client Bestt.',
    fix: 'Renseignez l’identifiant Bestt dans la fiche de l’entreprise, puis réessayez.',
  },
  ERR_ENTREPRISE_AMBIGUE_JOBBIZ: {
    label: 'Client Bestt rattaché à plusieurs entreprises',
    explanation: 'Plusieurs entreprises Jobbiz ont ce client Bestt.',
    fix: 'Ne laissez cet identifiant Bestt qu’à une seule entreprise, puis réessayez.',
  },
  ERR_BESTT_PDF_INDISPONIBLE: {
    label: 'PDF indisponible dans Bestt',
    explanation: 'Bestt n’a pas rendu le PDF de ces factures.',
    fix: 'Attendez quelques minutes, puis réessayez.',
  },
  ERR_STOCKAGE_PDF: {
    label: 'PDF non enregistré',
    explanation: 'Le PDF de ces factures n’a pas pu être rangé ou relu.',
    fix: 'Attendez quelques minutes, puis réessayez.',
  },
  ERR_PENNYLANE_DESACTIVE: {
    label: 'Envoi à Pennylane désactivé hors production',
    explanation: 'Hors production, rien n’est envoyé à Pennylane.',
    fix: 'Rien à corriger : normal hors production.',
  },
  ERR_ENTREPRISE_INTROUVABLE_PENNYLANE: {
    label: 'Entreprise introuvable dans Pennylane',
    explanation: 'Aucun client Pennylane ne correspond à l’entreprise.',
    fix: 'Créez le client dans Pennylane avec le SIRET de l’entreprise, puis réessayez.',
  },
  ERR_ENTREPRISE_AMBIGUE_PENNYLANE: {
    label: 'Plusieurs clients Pennylane pour l’entreprise',
    explanation:
      'Plusieurs clients Pennylane ont le SIRET ou le SIREN de l’entreprise.',
    fix: 'N’en gardez qu’un dans Pennylane, puis réessayez.',
  },
  // The rates of the group fill the explanation: see errorGroupTexts.
  ERR_TVA_TAUX_INCONNU: {
    label: 'Taux de TVA inconnu',
    explanation:
      'Le taux de {rates} appliqué sur ces factures n’est pas dans la table de Jobbiz.',
    explanationMany:
      'Les taux de {rates} appliqués sur ces factures ne sont pas dans la table de Jobbiz.',
    explanationNoRate:
      'Le taux de TVA de ces factures n’est pas dans la table de Jobbiz.',
    fix: 'Prévenez l’équipe technique pour qu’elle ajoute ce taux, puis réessayez.',
  },
  ERR_PENNYLANE_API: {
    label: 'Pennylane a refusé ou n’a pas répondu',
    explanation: 'Pennylane a refusé ces factures ou n’a pas répondu.',
    fix: 'Attendez quelques minutes, puis réessayez.',
  },
  ERR_PENNYLANE_CATEGORIE: {
    label: 'Catégorie non posée dans Pennylane',
    explanation:
      'Ces factures sont dans Pennylane ; réessayer ne pose que la catégorie.',
    // The retry sets the category the invoice got at the BeSTT step: it does
    // not read the company again, so fixing the company changes nothing here.
    fix: 'Réessayez : seule la catégorie sera posée.',
  },
  // Only for an invoice Pennylane already held (linked), whose due date an
  // admin chose: the retry sends that due date and nothing else.
  ERR_PENNYLANE_ECHEANCE: {
    label: 'Échéance non posée dans Pennylane',
    explanation:
      'Ces factures étaient déjà dans Pennylane ; l’échéance choisie pour elles n’y a pas été posée.',
    fix: 'Réessayez : seule l’échéance sera envoyée.',
  },
  // The retry never sends an appendix "to check" again: Pennylane may hold
  // it already, so it would be there twice.
  ERR_PENNYLANE_ANNEXE: {
    label: 'Annexe non envoyée dans Pennylane',
    explanation:
      'Ces factures sont importées, mais une de leurs annexes n’a pas pu être envoyée dans Pennylane.',
    fix: 'Si une annexe est « à vérifier dans Pennylane », cherchez-la d’abord dans Pennylane : supprimez-la dans Jobbiz, et ajoutez-la à nouveau si Pennylane ne l’a pas. Réessayez ensuite : seules les annexes en attente seront envoyées.',
  },
};

/** A code the screen does not know yet: shown as is, with these texts. */
export const UNKNOWN_ERROR_TEXTS = {
  explanation: 'Cette erreur n’a pas encore de description dans Jobbiz.',
  fix: 'Transmettez le code à l’équipe technique, puis réessayez.',
};

const isOwnKey = (table, key) =>
  Object.prototype.hasOwnProperty.call(table, key);

const percent = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

/** "8,5 %" for 8.5: BeSTT gives the rate in percent. */
export const formatVatRate = (rate) => `${percent.format(rate)} %`;

/** "a, b et c": `and` comes from the locales. */
export const joinWords = (words, and) =>
  words.length > 1
    ? `${words.slice(0, -1).join(', ')}${and}${words[words.length - 1]}`
    : words.join('');

const distinctVatRates = (invoices = []) => {
  const rates = invoices
    .map((invoice) => invoice.vatRate)
    .filter((rate) => typeof rate === 'number' && Number.isFinite(rate));
  return [...new Set(rates)].sort((a, b) => a - b);
};

/**
 * The label, explanation and fix of a group, read in the locales under
 * `invoiceJournal.errors.<code>` with this table as the default. `text` reads
 * a key of the `invoiceJournal` space.
 */
export const errorGroupTexts = (text, group) => {
  const { code } = group;
  if (!isOwnKey(INVOICE_ERROR_TEXTS, code)) {
    return {
      label: code,
      explanation: text(
        'unknownError.explanation',
        UNKNOWN_ERROR_TEXTS.explanation,
      ),
      fix: text('unknownError.fix', UNKNOWN_ERROR_TEXTS.fix),
    };
  }
  const defaults = INVOICE_ERROR_TEXTS[code];
  const read = (key) => text(`errors.${code}.${key}`, defaults[key]);
  let explanation = read('explanation');
  if (code === 'ERR_TVA_TAUX_INCONNU') {
    const rates = distinctVatRates(group.invoices).map(formatVatRate);
    if (rates.length === 0) explanation = read('explanationNoRate');
    else {
      explanation = fillText(
        rates.length === 1 ? explanation : read('explanationMany'),
        { rates: joinWords(rates, text('and', ' et ')) },
      );
    }
  }
  return { label: read('label'), explanation, fix: read('fix') };
};

/** "Juin 2026" for "2026-06". */
export const titleMonth = (label) =>
  label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
